import { DEFAULT_ROWS_PER_PAGE, type RowsPerPage } from '../../components/RowsPerPageSelect';
import type { BenchmarkState, ProgressReportStudent, ProgressReportWindow } from './students';
import { rosterAsOfLabel, type RosterMeta } from '../../core/rosterMeta';

export type BenchmarkFilter = 'all' | BenchmarkState;
export const BENCHMARK_FILTERS: readonly BenchmarkFilter[] = ['all', 'current', 'due', 'to_score', 'missed', 'none'];

export type ListQuery = {
  school: string | null;
  grade: string;
  teacher: string;
  class: string;
  benchmark: BenchmarkFilter;
  search: string;
  sort: 'asc' | 'desc';
  page: number;
  rowsPerPage: RowsPerPage;
};

export const ALL = '\u0000all';
export const ROWS_PER_PAGE: RowsPerPage = DEFAULT_ROWS_PER_PAGE;
export const DEFAULT_QUERY: ListQuery = { school: null, grade: ALL, teacher: ALL, class: ALL, benchmark: 'all', search: '', sort: 'asc', page: 1, rowsPerPage: ROWS_PER_PAGE };

export type BenchmarkCase = BenchmarkState;
const BENCHMARK_STATES: readonly BenchmarkState[] = ['current', 'due', 'to_score', 'missed', 'none'];

const collator = new Intl.Collator('en', { sensitivity: 'base' });

function unique(values: string[]): string[] {
  return [...new Set(values.filter((value) => value.trim() !== ''))].sort(collator.compare);
}

export const NO_SCHOOL = 'No school on file';

export function schoolOf(student: ProgressReportStudent): string {
  return student.school.trim() === '' ? NO_SCHOOL : student.school;
}

export function schoolsOf(students: ProgressReportStudent[]): string[] {
  const named = unique(students.map((student) => student.school));
  return students.some((student) => schoolOf(student) === NO_SCHOOL) ? [...named, NO_SCHOOL] : named;
}

export function defaultSchool(students: ProgressReportStudent[]): string | null {
  return schoolsOf(students)[0] ?? null;
}

export function teachersOf(student: ProgressReportStudent): string[] {
  return student.teachers.map((name) => name.trim()).filter((name) => name !== '');
}

export function teacherOptions(students: ProgressReportStudent[], school: string | null): string[] {
  return unique(schoolRows(students, school).flatMap(teachersOf));
}

export function gradeLabel(grade: string): string {
  const n = Number(grade);
  if (!Number.isInteger(n) || n < 1) return grade === '0' || /^k/i.test(grade) ? 'K' : grade;
  const suffix = n % 100 >= 11 && n % 100 <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th');
  return `${n}${suffix}`;
}

// One grade however the roster spells it, so "09" and "9" filter together.
export function gradeOf(student: ProgressReportStudent): string {
  const grade = student.grade.trim();
  if (grade === '0' || /^k/i.test(grade)) return 'K';
  const n = Number(grade);
  return Number.isInteger(n) && n >= 1 ? String(n) : grade;
}

function gradeRank(grade: string): number {
  if (grade === 'K') return 0;
  const n = Number(grade);
  return Number.isInteger(n) ? n : Number.POSITIVE_INFINITY;
}

export function gradeOptions(students: ProgressReportStudent[], school: string | null): { value: string; label: string }[] {
  return unique(schoolRows(students, school).map(gradeOf))
    .sort((a, b) => gradeRank(a) - gradeRank(b) || collator.compare(a, b))
    .map((grade) => ({ value: grade, label: gradeLabel(grade) }));
}

export const NO_CLASS = 'No class';

export function classesOf(student: ProgressReportStudent): string[] {
  return (student.classes ?? []).map((name) => name.trim()).filter((name) => name !== '');
}

export function classLine(student: ProgressReportStudent): string {
  return (student.class ?? '').trim();
}

export function classOptions(students: ProgressReportStudent[], school: string | null): string[] {
  const rows = schoolRows(students, school);
  const sis = unique(rows.map((student) => (student.sisClass ?? '').trim()));
  const sections = unique(rows.flatMap(classesOf)).filter((name) => !sis.includes(name));
  return [...sis, ...sections];
}

export function schoolRows(students: ProgressReportStudent[], school: string | null): ProgressReportStudent[] {
  if (school === null) return [];
  return students.filter((student) => schoolOf(student) === school);
}

export function benchmarkCase(student: ProgressReportStudent): BenchmarkCase {
  if (student.state !== undefined && BENCHMARK_STATES.includes(student.state)) return student.state;
  if (student.latestBenchmark === null) return 'none';
  return student.inCurrentWindow ? 'current' : 'missed';
}

export const NO_NAME = 'Name not on file';

export function studentName(student: ProgressReportStudent): string {
  const last = student.lastName.trim();
  const first = student.firstName.trim();
  if (last === '' && first === '') return NO_NAME;
  if (last === '' || first === '') return last || first;
  return `${last}, ${first}`;
}

function normalise(text: string): string {
  return text.toLowerCase().replace(/[,\s]+/g, ' ').trim();
}

function matchesSearch(student: ProgressReportStudent, search: string): boolean {
  const needle = normalise(search);
  if (needle === '') return true;
  const first = normalise(student.firstName);
  const last = normalise(student.lastName);
  return `${last} ${first}`.includes(needle) || `${first} ${last}`.includes(needle);
}

export function visibleRows(students: ProgressReportStudent[], query: ListQuery): ProgressReportStudent[] {
  const rows = schoolRows(students, query.school).filter((student) => {
    if (query.grade !== ALL && gradeOf(student) !== query.grade) return false;
    if (query.teacher !== ALL && !teachersOf(student).includes(query.teacher)) return false;
    if (query.class !== ALL && !classesOf(student).includes(query.class)) return false;
    if (query.benchmark === 'none' && student.latestBenchmark !== null) return false;
    if (query.benchmark !== 'all' && query.benchmark !== 'none' && benchmarkCase(student) !== query.benchmark) return false;
    return matchesSearch(student, query.search);
  });
  const direction = query.sort === 'asc' ? 1 : -1;
  return rows.sort(
    (a, b) => direction * (collator.compare(a.lastName, b.lastName) || collator.compare(a.firstName, b.firstName)),
  );
}

export function pageRows<T>(rows: T[], page: number, perPage: number = ROWS_PER_PAGE): T[] {
  return rows.slice((page - 1) * perPage, page * perPage);
}

export function pageCount(total: number, perPage: number = ROWS_PER_PAGE): number {
  return Math.max(1, Math.ceil(total / perPage));
}

export function benchmarkFilterOptions(current: ProgressReportWindow | null): { value: BenchmarkFilter; label: string }[] {
  if (current === null) {
    return [
      { value: 'all', label: 'All' },
      { value: 'none', label: 'No benchmark yet' },
    ];
  }
  const phase = current.phase ?? 'undated';
  const options: { value: BenchmarkFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'current', label: current.label },
  ];
  if (phase === 'open' || phase === 'mixed') options.push({ value: 'due', label: `${current.short} due` });
  if (phase !== 'undated') options.push({ value: 'to_score', label: 'To score' });
  if (phase !== 'open') options.push({ value: 'missed', label: `Missed ${current.short}` });
  options.push({ value: 'none', label: 'No benchmark yet' });
  return options;
}

function countWord(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

export function headerMeta(
  students: ProgressReportStudent[],
  school: string | null,
  current: ProgressReportWindow | null,
  roster?: RosterMeta | null,
): string {
  const rows = schoolRows(students, school);
  const count = countWord(rows.length, 'student', 'students');
  const note = rosterAsOfLabel(roster);
  const withNote = (text: string) => (note === null ? text : `${text} · ${note}`);
  if (current === null || school === null) return withNote(count);
  const missing = rows.filter((student) => benchmarkCase(student) !== 'current').length;
  return withNote(`${count} · ${missing} without a ${current.short} benchmark`);
}

export function printLabel(selected: number): string {
  if (selected === 0) return 'Print reports';
  return `Print ${countWord(selected, 'report', 'reports')}`;
}

export function emptyBody(query: ListQuery): string {
  const school = query.school ?? '';
  if (query.search.trim() !== '') return `No one named “${query.search.trim()}” at ${school}.`;
  return `No students at ${school} match these filters.`;
}

export function clearedQuery(query: ListQuery): ListQuery {
  return { ...DEFAULT_QUERY, school: query.school, sort: query.sort };
}

export function printOrder(selected: string[], students: ProgressReportStudent[], sort: ListQuery['sort'] = 'asc'): string[] {
  const byId = new Map(students.map((student) => [student.studentId, student]));
  const listed = selected.filter((id) => byId.has(id));
  const unlisted = selected.filter((id) => !byId.has(id));
  const direction = sort === 'asc' ? 1 : -1;
  listed.sort((a, b) => {
    const sa = byId.get(a)!;
    const sb = byId.get(b)!;
    return direction * (collator.compare(sa.lastName, sb.lastName) || collator.compare(sa.firstName, sb.firstName));
  });
  return [...listed, ...unlisted];
}
